import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

interface NavigationItem {
  label: string;
  path: string;
  icon: string;
  tooltip: string;
  children?: Array<{
    label: string;
    path: string;
  }>;
}

interface MainSidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
}

const MainSidebar = ({ isCollapsed = false, onToggle }: MainSidebarProps) => {
  const location = useLocation();
  const { userProfile } = useAuth();
  const [expandedItems, setExpandedItems] = useState<string[]>(['Quotes']);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const allMenuItems = [
  {
    label: 'Customers',
    path: '/customers-management-page',
    icon: 'Briefcase',
    tooltip: 'Manage customer accounts and information'
  },
  {
    label: 'Quotes',
    path: '/quotes-overview-dashboard',
    icon: 'FileText',
    tooltip: 'Manage quote pipeline and create new quotes',
    children: [
    { label: 'Overview', path: '/quotes-overview-dashboard' },
    { label: 'New Quote', path: '/quote-editor-interface' },
    { label: 'Activity Audit', path: '/quote-activity-audit-dashboard' }]

  },
  {
    label: 'Products',
    path: '/product-catalog-management',
    icon: 'Package',
    tooltip: 'Manage product catalog and pricing'
  },
  {
    label: 'Users',
    path: '/user-management-dashboard',
    icon: 'Users',
    tooltip: 'Manage user accounts and permissions',
    adminOnly: true
  }];

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter(item => {
    if (item.adminOnly) {
      return userProfile?.role === 'admin';
    }
    return true;
  });

  const toggleExpanded = (label: string) => {
    setExpandedItems((prev) =>
    prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]
    );
  };

  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (item: NavigationItem) => {
    if (item.children) {
      return item.children.some((child) => location.pathname === child.path);
    }
    return location.pathname === item.path;
  };

  const closeMobileMenu = () => setIsMobileOpen(false);

  const renderNavigationItem = (item: NavigationItem) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.label);
    const parentActive = isParentActive(item);

    return (
      <div key={item.label} className="mb-1">
        {hasChildren ?
        <>
            <button
            onClick={() => toggleExpanded(item.label)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-150 ease-out group ${
            parentActive ?
            'bg-primary/10 text-primary' : 'text-text-primary hover:bg-muted hover:text-primary'}`
            }
            title={item.tooltip}>

              <div className="flex items-center gap-3">
                <Icon
                name={item.icon}
                size={20}
                className={parentActive ? 'text-primary' : 'text-text-secondary group-hover:text-primary'} />

                {!isCollapsed && <span className="font-medium text-sm">{item.label}</span>}
              </div>
              {!isCollapsed &&
            <Icon
              name="ChevronDown"
              size={16}
              className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />

            }
            </button>
            {!isCollapsed && isExpanded &&
          <div className="ml-9 mt-1 space-y-1">
                {item.children.map((child) =>
            <Link
              key={child.path}
              to={child.path}
              onClick={closeMobileMenu}
              className={`block px-4 py-2 rounded-lg text-sm transition-all duration-150 ease-out ${
              isActive(child.path) ?
              'bg-primary/10 text-primary font-medium' : 'text-text-secondary hover:bg-muted hover:text-primary'}`
              }>

                    {child.label}
                  </Link>
            )}
              </div>
          }
          </> :

        <Link
          to={item.path}
          onClick={closeMobileMenu}
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-150 ease-out group ${
          isActive(item.path) ?
          'bg-primary/10 text-primary' : 'text-text-primary hover:bg-muted hover:text-primary'}`
          }
          title={item.tooltip}>

            <Icon
            name={item.icon}
            size={20}
            className={isActive(item.path) ? 'text-primary' : 'text-text-secondary group-hover:text-primary'} />

            {!isCollapsed && <span className="font-medium text-sm">{item.label}</span>}
          </Link>
        }
      </div>);

  };

  return (
    <>
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-[250] p-2 bg-card rounded-lg shadow-md"
        aria-label="Toggle mobile menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} />
      </button>

      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-[200]"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed lg:fixed top-0 left-0 h-screen bg-card border-r border-border z-[200] transition-all duration-300 ease-out ${
          isCollapsed ? 'w-20' : 'w-60'
        } ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-4 py-6 border-b border-border">
            <div className="flex items-center gap-3">
              {!isCollapsed ? (
                <img 
                  src="/assets/images/evoqia-logo_-nb-1766401973047.png" 
                  alt="Evoqia logo with geometric green and gray icon element and modern typography"
                  className="h-8 object-contain"
                />
              ) : (
                <img 
                  src="/assets/images/evoqia-logo_-nb-1766401973047.png" 
                  alt="Evoqia logo icon"
                  className="h-8 w-8 object-contain"
                />
              )}
            </div>
            {onToggle && (
              <button
                onClick={onToggle}
                className="hidden lg:block p-1 hover:bg-muted rounded transition-colors duration-150"
                aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <Icon 
                  name={isCollapsed ? 'ChevronsRight' : 'ChevronsLeft'} 
                  size={20} 
                />
              </button>
            )}
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-4" role="navigation">
            {menuItems.map(renderNavigationItem)}
          </nav>
        </div>
      </aside>
    </>
  );
};

export default MainSidebar;